import React from 'react';
import cx from 'classnames';

import Touchable, { TouchableProps } from '../Touchable';
import Checkmark from '../Icons/Checkmark';
import useUniqueId from '../utils/useUniqueId';

import styles from './Dropdown.module.scss';
import Text, { TextProps } from '../Text';

export interface DropdownItem extends TouchableProps {
  label: string;
  value: string;
  icon?: React.ReactElement;
}

export interface Props {
  block?: boolean;
  children?: Array<React.ReactElement>;
  className?: Parameters<typeof cx>[0];
  id?: string;
  isOpen: boolean;
  onChange?: (v: DropdownItem) => void;
  onEscape?: () => void;
  options?: Array<DropdownItem>;
  target: React.ReactElement;
  value?: DropdownItem;
  fontSize?: TextProps['size'];
  wrapperClassName?: Parameters<typeof cx>[0];
}

export const Dropdown: React.FC<Props> = ({
  block,
  children,
  className,
  id,
  isOpen,
  onChange,
  onEscape,
  options,
  target,
  value,
  fontSize = 16,
  wrapperClassName,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const uniqueId = useUniqueId('dropdown');
  // use supplied id, or auto-generated id if not supplied
  const dropdownId = id || uniqueId;

  React.useEffect(() => {
    if (isOpen && onEscape) {
      const clickOutsideHandler = (event: MouseEvent) => {
        if (
          ref.current &&
          event.target instanceof Node &&
          !ref.current.contains(event.target)
        ) {
          onEscape();
        }
      };
      document.addEventListener('click', clickOutsideHandler);

      const escapeKeyHandler = (event: KeyboardEvent) => {
        if (event.keyCode === 27) {
          onEscape();
        }
      };
      document.addEventListener('keyup', escapeKeyHandler);

      return () => {
        document.removeEventListener('click', clickOutsideHandler);
        document.removeEventListener('keyup', escapeKeyHandler);
      };
    }

    return () => {};
  }, [isOpen, onEscape]);

  return (
    <div
      className={cx(
        styles.dropdownWrapper,
        block && styles.block,
        wrapperClassName,
      )}
      ref={ref}
    >
      {React.cloneElement(target, {
        'aria-expanded': isOpen,
        ariaExpanded: isOpen,
        'aria-haspopup': true,
        ariaHasPopup: true,
        'aria-controls': dropdownId,
        ariaControls: dropdownId,
      })}
      <div
        role='menu'
        id={dropdownId}
        className={cx(styles.dropdown, isOpen && styles.isOpen, className)}
      >
        <ul className={styles.list}>
          {options &&
            !children &&
            options.map((o, idx) => (
              <li className={styles.item} key={idx}>
                <Touchable
                  {...o}
                  className={cx(styles.touchable, o.className)}
                  onClick={() => onChange && onChange(o)}
                  ariaCurrent={value && value.value === o.value}
                >
                  <span className={styles.touchableContent}>
                    {o.icon != null &&
                      React.cloneElement(o.icon, {
                        className: cx(o.icon.props.className, styles.icon),
                      })}
                    <Text
                      size={fontSize}
                      as='span'
                      contrast={o.disabled ? 'inverse-low' : 'high'}
                    >
                      {o.label}
                    </Text>
                  </span>
                  {value && value.value === o.value && <Checkmark />}
                </Touchable>
              </li>
            ))}
          {children &&
            !options &&
            children.map((c, idx) => (
              <li className={styles.item} key={idx}>
                {React.cloneElement(c, {
                  className: cx(styles.touchable, c.props.className),
                })}
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
};

Dropdown.displayName = 'Dropdown';
export default Dropdown;
export { styles };
